"use client"
import { themeConfig } from "@/lib/theme-config"
import type { Step } from "@/lib/types"

import { Button } from "@/components/ui/button"
import { Check, Copy, QrCode, X } from "lucide-react"
import QRCode from "qrcode"
import { useEffect, useState } from "react"
import StepSkeleton from "./step-skeleton"

interface PackageType {
    package_type: string
    id: string
    package_category: string
}

interface PackagingGuideProps {
    productName: string
    packagingTypes: PackageType[],
    allPackageTypes: PackageType[]
    selectedPackagingType?: string
    onPackagingTypeSelect?: (packagingType: string) => void
}

export default function PackagingGuide({ productName, packagingTypes, allPackageTypes, selectedPackagingType, onPackagingTypeSelect }: PackagingGuideProps) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://your-api.com'
    const [selectedType, setSelectedType] = useState<string | null>(null)
    const [showMoreOptions, setShowMoreOptions] = useState(false)
    const [selectedMoreOptionType, setSelectedMoreOptionType] = useState<string | null>(null)
    const [steps, setSteps] = useState<Step[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isStreamComplete, setIsStreamComplete] = useState(false)
    const [showQRCode, setShowQRCode] = useState(false)
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("")
    const [isGeneratingQR, setIsGeneratingQR] = useState(false)
    const [isCopied, setIsCopied] = useState(false)

    // Initialize selected type from URL parameter
    useEffect(() => {
        if (selectedPackagingType && packagingTypes.length > 0) {
            const foundType = packagingTypes.find(type => type.package_type === selectedPackagingType)
            if (foundType) {
                setSelectedType(selectedPackagingType)
                // Auto-trigger the selection if it's from URL
                handlePackagingTypeSelect(selectedPackagingType)
            }
        }
    }, [selectedPackagingType, packagingTypes])

    // Build a base-aware URL for package type images
    const getPackageTypeImageSrc = (typeId: string): string => {
        const configuredBase = (import.meta.env as any).VITE_ASSET_BASE as string | undefined
        const normalizedBase = configuredBase
            ? configuredBase.replace(/\/$/, "")
            : `${import.meta.env.BASE_URL}images`.replace(/\/$/, "")
        return `${normalizedBase}/package_types/${typeId}.svg`
    }

    // Helper function to categorize package types
    const categorizePackageTypes = () => {
        const categories = {
            FLEXIBLE: [] as PackageType[],
            RIGID: [] as PackageType[],
            'SEMI-RIGID': [] as PackageType[],
            'BULK/INDUSTRIAL': [] as PackageType[]
        }

        allPackageTypes.forEach(pkgType => {
            // If package_type is "Intermediate Bulk Containers (IBCs) / Totes for Bulk/Industrial Packaging"
            // set to IBCs/Tote
            if (pkgType.package_type.includes("Intermediate Bulk Containers")) {
                pkgType.package_type = "IBCs/Tote"
            }

            // Override package_type values to remove "Bulk Industrial" or "Bulk/Industrial"
            if (pkgType.package_type.includes("Bulk Industrial") || pkgType.package_type.includes("Bulk/Industrial")) {
                pkgType.package_type = pkgType.package_type
                    .replace("Bulk Industrial", "")
                    .replace("Bulk/Industrial", "")
                    .trim()
            }

            // Remove "Packaging" from the end of package_type values
            if (pkgType.package_type.endsWith("Packaging")) {
                pkgType.package_type = pkgType.package_type
                    .replace(/Packaging$/, "")
                    .trim()
            }

            let category: keyof typeof categories
            switch (pkgType.package_category) {
                case 'FLEXIBLE':
                    category = 'FLEXIBLE'
                    break
                case 'RIGID':
                    category = 'RIGID'
                    break
                case 'SEMI-RIGID':
                    category = 'SEMI-RIGID'
                    break
                case 'BULK-INDUSTRIAL':
                    category = 'BULK/INDUSTRIAL'
                    break
                default:
                    return // Skip unknown categories
            }
            categories[category].push(pkgType)
        })

        // Sort each category alphabetically
        Object.keys(categories).forEach(category => {
            categories[category as keyof typeof categories].sort((a, b) => 
                a.package_type.localeCompare(b.package_type)
            )
        })

        return categories
    }

    const handlePackagingTypeSelect = async (type: string) => {
        setSelectedType(type)
        setShowMoreOptions(false)
        setSelectedMoreOptionType(null)
        setIsLoading(true)
        setError(null)
        setSteps([])
        setIsStreamComplete(false)
        
        // Notify parent component about the selection
        if (onPackagingTypeSelect) {
            onPackagingTypeSelect(type)
        }

        try {
            // Start the stream
            const response = await fetch(`${baseUrl}/api/generate-packaging-steps`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: productName,
                    package_type: type
                })
            })

            if (!response.body) {
                throw new Error("Stream not available")
            }

            // Create a reader from the stream
            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ""

            // Create a cache to track which steps we've already fetched equipment for
            const processedStepIds = new Set<number>()

            // Read from the stream
            while (true) {
                const { done, value } = await reader.read()

                if (done) {
                    // Process any remaining data in the buffer
                    if (buffer.trim()) {
                        try {
                            const steps: Step[] = JSON.parse(buffer.trim())
                            console.log(steps)
                            setSteps((prevSteps) => {
                                const stepsMap = new Map(prevSteps.map(s => [s.id, s]))
                                return steps.map(s => ({
                                    ...s,
                                    equipment: stepsMap.get(s.id)?.equipment || []
                                }))
                            })
                        } catch (e) {
                            console.error("Error parsing JSON from buffer:", e)
                        }
                    }
                    setIsStreamComplete(true)
                    break
                }

                // Decode the chunk and add to buffer
                const chunk = decoder.decode(value, { stream: true })
                buffer += chunk

                // Process complete JSON objects from the buffer
                const lines = buffer.split("\n")
                buffer = lines.pop() || "" // Keep the last incomplete line in the buffer

                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            const steps: Step[] = JSON.parse(line)
                            // Update steps while preserving existing equipment
                            setSteps((prevSteps) => {
                                const stepsMap = new Map(prevSteps.map(s => [s.id, s]))
                                return steps.map(s => ({
                                    ...s,
                                    equipment: stepsMap.get(s.id)?.equipment || []
                                }))
                            })

                            // Then fetch equipment for each step that has a description
                            for (const step of steps) {
                                // Only fetch if we haven't processed this step ID before
                                if (step.description?.trim() && !processedStepIds.has(step.id)) {
                                    processedStepIds.add(step.id)

                                    // Make the equipment fetch non-blocking
                                    fetch(`${baseUrl}/api/get-related-equipment`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify({ query: step.description })
                                    })
                                        .then(response => response.json())
                                        .then(equipment => {
                                            setSteps((prevSteps) => {
                                                console.log('Previous steps:', prevSteps)
                                                console.log('Updating equipment for step:', step.id)
                                                console.log('New equipment data:', equipment)

                                                const updatedSteps = prevSteps.map((s) => {
                                                    if (s.id === step.id) {
                                                        const updatedStep = { ...s, equipment: equipment }
                                                        console.log('Updated step:', updatedStep)
                                                        return updatedStep
                                                    }
                                                    return s
                                                })

                                                console.log('All updated steps:', updatedSteps)
                                                return updatedSteps
                                            })
                                        })
                                        .catch(error => {
                                            console.error('Error fetching equipment:', error)
                                        })
                                }
                            }
                        } catch (e) {
                            console.error("Error parsing JSON:", e, line)
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Error streaming packaging steps:", err)
            setError("Failed to load packaging steps. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleMoreOptionsClick = () => {
        setSelectedType(null)
        setSelectedMoreOptionType(null)
        setShowMoreOptions(!showMoreOptions)
        setSteps([])
        setIsStreamComplete(false)
    }

    const handleMoreOptionTypeSelect = async (type: string) => {
        setSelectedMoreOptionType(type)
        setIsLoading(true)
        setError(null)
        setSteps([])
        setIsStreamComplete(false)
        
        // Notify parent component about the selection
        if (onPackagingTypeSelect) {
            onPackagingTypeSelect(type)
        }

        try {
            // Start the stream
            const response = await fetch(`${baseUrl}/api/generate-packaging-steps`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: productName,
                    package_type: type
                })
            })

            if (!response.body) {
                throw new Error("Stream not available")
            }

            // Create a reader from the stream
            const reader = response.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ""

            // Create a cache to track which steps we've already fetched equipment for
            const processedStepIds = new Set<number>()

            // Read from the stream
            while (true) {
                const { done, value } = await reader.read()

                if (done) {
                    // Process any remaining data in the buffer
                    if (buffer.trim()) {
                        try {
                            const steps: Step[] = JSON.parse(buffer.trim())
                            console.log(steps)
                            setSteps((prevSteps) => {
                                const stepsMap = new Map(prevSteps.map(s => [s.id, s]))
                                return steps.map(s => ({
                                    ...s,
                                    equipment: stepsMap.get(s.id)?.equipment || []
                                }))
                            })
                        } catch (e) {
                            console.error("Error parsing JSON from buffer:", e)
                        }
                    }
                    setIsStreamComplete(true)
                    break
                }

                // Decode the chunk and add to buffer
                const chunk = decoder.decode(value, { stream: true })
                buffer += chunk

                // Process complete JSON objects from the buffer
                const lines = buffer.split('\n')
                buffer = lines.pop() || ""

                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            const steps: Step[] = JSON.parse(line.trim())
                            console.log(steps)
                            setSteps((prevSteps) => {
                                const stepsMap = new Map(prevSteps.map(s => [s.id, s]))
                                return steps.map(s => ({
                                    ...s,
                                    equipment: stepsMap.get(s.id)?.equipment || []
                                }))
                            })

                            // Fetch equipment for new steps
                            for (const step of steps) {
                                if (!processedStepIds.has(step.id)) {
                                    processedStepIds.add(step.id)
                                    
                                    // Only fetch if we haven't processed this step ID before and step has description
                                    if (step.description?.trim()) {
                                        // Make the equipment fetch non-blocking
                                        fetch(`${baseUrl}/api/get-related-equipment`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify({ query: step.description })
                                        })
                                            .then(response => response.json())
                                            .then(equipment => {
                                                setSteps((prevSteps) => {
                                                    console.log('Previous steps:', prevSteps)
                                                    console.log('Updating equipment for step:', step.id)
                                                    console.log('New equipment data:', equipment)

                                                    const updatedSteps = prevSteps.map((s) => {
                                                        if (s.id === step.id) {
                                                            const updatedStep = { ...s, equipment: equipment }
                                                            console.log('Updated step:', updatedStep)
                                                            return updatedStep
                                                        }
                                                        return s
                                                    })

                                                    console.log('All updated steps:', updatedSteps)
                                                    return updatedSteps
                                                })
                                            })
                                            .catch(error => {
                                                console.error('Error fetching equipment:', error)
                                            })
                                    }
                                }
                            }
                        } catch (e) {
                            console.error("Error parsing JSON:", e, line)
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Error streaming packaging steps:", err)
            setError("Failed to load packaging steps. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }

    // Calculate the expected number of steps (for skeleton loading)
    const expectedSteps = selectedType === "FLEXIBLE PACKAGING" || selectedType === "FOLDING CARTONS" ? 4 : 0
    const skeletonCount = Math.max(0, expectedSteps - steps.length)

    // Generate QR code
    const generateQRCode = async () => {
        if (typeof window === 'undefined') return
        
        setIsGeneratingQR(true)
        try {
            const url = window.location.href
            const dataUrl = await QRCode.toDataURL(url, {
                width: 200,
                margin: 2,
                color: {
                    dark: themeConfig.colors.primary,
                    light: '#FFFFFF'
                }
            })
            setQrCodeDataUrl(dataUrl)
            setShowQRCode(true)
        } catch (err) {
            console.error('Error generating QR code:', err)
        } finally {
            setIsGeneratingQR(false)
        }
    }

    // Copy URL to clipboard
    const handleCopyUrl = async () => {
        if (typeof window === 'undefined') return
        
        try {
            await navigator.clipboard.writeText(window.location.href)
            setIsCopied(true)
            setTimeout(() => setIsCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy URL:', err)
        }
    }



    return (
        <div>
            <h3 className="text-lg font-medium mb-4 text-center" style={{ color: themeConfig.colors.primary }}>
                Which type of {productName} packaging are you looking for?
            </h3>

            {packagingTypes.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-gray-600">Loading packaging options...</p>
                </div>
            ) : (
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 mb-6">
                    {packagingTypes.map((type) => {
                    const isSelected = selectedType === type.package_type
                    return (
                        <button
                            key={`${type.id}-${type.package_type.toLowerCase().replace(/[^a-z0-9]/g, '')}`}
                            onClick={() => handlePackagingTypeSelect(type.package_type)}
                            disabled={isLoading}
                            className={`group relative flex flex-col items-center justify-between rounded-md border border-gray-200 shadow-sm p-3 sm:p-4 transition-colors duration-150 flex-1 min-w-0 ${
                                isSelected
                                    ? ''
                                    : 'hover:shadow-md hover:border-gray-300'
                            } ${
                                isLoading ? 'opacity-60 cursor-not-allowed' : ''
                            }`}
                            style={{ backgroundColor: '#f4f6f7' }}
                        >
                            <div className="flex items-center justify-center h-16 sm:h-24 w-full">
                                <img
                                    src={getPackageTypeImageSrc(type.id)}
                                    alt={type.package_type}
                                    className="max-h-16 sm:max-h-20 w-auto object-contain drop-shadow-sm"
                                    loading="lazy"
                                />
                            </div>
                            <div className="mt-2 sm:mt-3 w-full text-center">
                                <span
                                    className={`block text-sm font-semibold uppercase tracking-wide`}
                                    style={{ color: themeConfig.colors.primary }}
                                >
                                    {type.package_type}
                                </span>
                            </div>
                            {isSelected && (
                                <div
                                    className="absolute inset-0 rounded-md"
                                    style={{ boxShadow: `inset 0 0 0 2px ${themeConfig.colors.primary}` }}
                                />
                            )}
                        </button>
                    )
                })}
                
                {/* More Options Card */}
                <button
                    onClick={handleMoreOptionsClick}
                    disabled={isLoading}
                    className={`group relative flex flex-col items-center justify-center rounded-md border border-gray-200 shadow-sm p-4 transition-colors duration-150 flex-1 min-w-0 ${
                        showMoreOptions
                            ? ''
                            : 'hover:shadow-md hover:border-gray-300'
                    } ${
                        isLoading ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    style={{ backgroundColor: '#f4f6f7' }}
                >
                    <div className="mt-3 w-full text-center">
                        <span
                            className={`block text-sm font-semibold uppercase tracking-wide`}
                            style={{ color: themeConfig.colors.primary }}
                        >
                            More Options...
                        </span>
                    </div>
                    {showMoreOptions && (
                        <div
                            className="absolute inset-0 rounded-md"
                            style={{ boxShadow: `inset 0 0 0 2px ${themeConfig.colors.primary}` }}
                        />
                    )}
                </button>
            </div>
            )}

            {/* Hidden full-width card for More Options */}
            {showMoreOptions && (
                <div className="w-full mb-6">
                    <div 
                        className="border border-gray-200 rounded-md p-6"
                        style={{ backgroundColor: '#f4f6f7' }}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            {Object.entries(categorizePackageTypes()).map(([category, packageTypes]) => (
                                <div key={category} className="space-y-3">
                                    <h4 
                                        className="font-bold text-lg mb-3"
                                        style={{ color: themeConfig.colors.primary }}
                                    >
                                        {category}
                                    </h4>
                                    <div className="space-y-2">
                                        {packageTypes.map((pkgType) => {
                                            const isSelected = selectedMoreOptionType === pkgType.package_type                                            
                                            const key = `${pkgType.package_type}-${pkgType.id}`
                                            return (
                                                <div key={key} className="flex flex-row">                                                    
                                                    <button
                                                        onClick={() => handleMoreOptionTypeSelect(pkgType.package_type)}
                                                        disabled={isLoading}
                                                        className={`w-fit text-left underline rounded transition-colors duration-150 basis-auto -ml-1 px-1 leading-[20px] ${
                                                            isSelected
                                                                ? 'font-bold scale-105'
                                                                : 'hover:bg-gray-100'
                                                        } ${
                                                            isLoading ? 'opacity-60 cursor-not-allowed' : ''
                                                        }`}
                                                        style={{
                                                            color: isSelected ? themeConfig.colors.primary : '#374151',
                                                            //textDecoration: isSelected ? 'underline' : 'none'
                                                        }}
                                                    >
                                                        {pkgType.package_type}
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {(selectedType || selectedMoreOptionType) && (
                <div 
                    className="space-y-4 min-h-[400px]"
                    style={{ 
                        minHeight: isLoading || !isStreamComplete ? '400px' : 'auto',
                        transition: 'min-height 0.3s ease-in-out'
                    }}
                >
                    {steps.map((step) => (
                        <div
                            key={step.id}
                            className="border border-gray-200 rounded-md p-4 animate-fadeIn"
                            style={{ 
                                backgroundColor: `${themeConfig.colors.primary}05`,
                                minHeight: '120px', // Ensure consistent step height
                                transition: 'all 0.3s ease-in-out'
                            }}
                        >
                            <p className="font-medium mb-2" style={{ color: themeConfig.colors.primary }}>
                                Step {step.id}: {step.description}
                            </p>
                            <div>
                                <p className="text-sm text-gray-600">Equipment Categories:</p>
                                <div className="flex flex-wrap gap-2 mt-1 min-h-[32px]">
                                    {step.equipment.map((equipment) => {
                                        return (
                                            <a
                                                href={equipment.url}
                                                target="_blank"
                                                key={equipment.id}
                                                className="px-3 py-1 text-white text-sm rounded-full inline-flex items-center"
                                                style={{ backgroundColor: themeConfig.colors.primary }}
                                            >
                                                {equipment.name}
                                            </a>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Show skeletons for steps that haven't loaded yet */}
                    {!isStreamComplete &&
                        skeletonCount > 0 &&
                        Array(skeletonCount)
                            .fill(0)
                            .map((_, i) => <StepSkeleton key={`skeleton-${i}`} />)}

                    {error && <p className="text-red-500 py-4">{error}</p>}

                    {isStreamComplete && steps.length === 0 && !error && (
                        <p className="text-center py-4">No packaging steps found for {selectedType || selectedMoreOptionType}.</p>
                    )}

                    {/* QR Code Section - only show when steps are complete and there are results */}
                    {isStreamComplete && steps.length > 0 && !error && (
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <div className="text-center">
                                {!showQRCode ? (
                                    <Button
                                        onClick={generateQRCode}
                                        disabled={isGeneratingQR}
                                        variant="outline"
                                        className="flex items-center gap-2 mx-auto"
                                        style={{ 
                                            borderColor: themeConfig.colors.primary,
                                            color: themeConfig.colors.primary 
                                        }}
                                    >
                                        <QrCode className="h-4 w-4" />
                                        {isGeneratingQR ? 'Generating QR...' : 'Scan QR to open this page on your phone'}
                                    </Button>
                                ) : (
                                    <div className="inline-block p-6 bg-white border border-gray-200 rounded-lg shadow-lg">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: themeConfig.colors.primary }}>
                                                <QrCode className="h-5 w-5" />
                                                Scan QR Code
                                            </h3>
                                            <button
                                                onClick={() => setShowQRCode(false)}
                                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                        
                                        <div className="flex flex-col items-center space-y-4">
                                            <img 
                                                src={qrCodeDataUrl} 
                                                alt="QR Code" 
                                                className="w-48 h-48 border border-gray-200 rounded-lg"
                                            />
                                            <div className="text-center">
                                                <p className="text-sm text-gray-600 mb-3 break-all max-w-xs">
                                                    {typeof window !== 'undefined' ? window.location.href : ''}
                                                </p>
                                                <Button
                                                    onClick={handleCopyUrl}
                                                    variant="outline"
                                                    size="sm"
                                                    className="flex items-center gap-2"
                                                >
                                                    {isCopied ? (
                                                        <>
                                                            <Check className="h-4 w-4" />
                                                            Copied!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy className="h-4 w-4" />
                                                            Copy URL
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

        </div>
    )
}